import { useQuery } from '@tanstack/react-query';

import { asApiError, ImpersonationBroker } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { FieldKey } from '../../viewModels/form';
import type { IPhoneCallOutcomeGroup } from '../../viewModels/phonecall';
import { queryKeys } from '../data/queryKeys';
import type { PhoneCallOutcome, PhoneCallOutcomeGroup } from './types';

export type UseOutcomesResult = {
	/**
	 * Flattened curated list of shortcuts the picker renders at the top level.
	 * Mirrors `QueueViewModel.outcomeShortcuts` (queue.ts:287-296):
	 *   topShortcuts.children + meetingOutcomes.children +
	 *   isShortCut top-level groups (excluding BadLead) + shortcuts.children.
	 *
	 * If `Connected` is not surfaced by that list (legacy queue surfaces it
	 * only when `isShortCut === true`), we prepend it so the rep can drill
	 * into Connected's children, matching the legacy "Connected →" entry.
	 */
	outcomeShortcuts: PhoneCallOutcome[];
	/** Connected group used as the drill-down source. */
	connectedGroup: PhoneCallOutcomeGroup | undefined;
	/** BadLead outcome — clicking it routes to skip mode in `useWrapup`. */
	badLeadOutcome: PhoneCallOutcome | undefined;
	isLoading: boolean;
	error: Error | null;
};

/**
 * Fetches the per-lead phone-call outcome groups via
 * `GET lead/{companyId}/PhoneCallOutcome`. Mirrors `QueueViewModel.loadPhoneCallOutcomes`
 * but exposes the result as a clean react-query hook — no MobX, no view-model.
 *
 * The legacy queue does NOT render raw outcome groups at the top level. It
 * renders a flattened, curated `outcomeShortcuts` list that combines:
 *   - children of the `TopShortcuts` group (rendered first),
 *   - children of the `MeetingOutcomes` group,
 *   - top-level groups marked `isShortCut` (excluding `BadLead`),
 *   - children of the `Shortcuts` group (rendered last).
 *
 * We mirror that here so consumers get the legacy ordering for free.
 */
export function useOutcomes(companyId: string | undefined): UseOutcomesResult {
	const userSession = useUserSession();

	const query = useQuery<
		{
			outcomeShortcuts: PhoneCallOutcome[];
			connectedGroup: PhoneCallOutcomeGroup | undefined;
			badLeadOutcome: PhoneCallOutcome | undefined;
		},
		Error
	>({
		queryKey: queryKeys.focusedView.outcomes(companyId),
		enabled: !!companyId,
		queryFn: async () => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<IPhoneCallOutcomeGroup[]>(
				ImpersonationBroker.composeApiUrl({ urlPath: `lead/${companyId}/PhoneCallOutcome` }),
				'GET'
			);
			if (!result.success) {
				throw asApiError(result);
			}

			const groups = result.value ?? [];
			const topShortcuts = groups.find(g => g.name === FieldKey.TopShortcuts);
			const shortcuts = groups.find(g => g.name === FieldKey.Shortcuts);
			// Legacy splits the loaded groups into shortcut groups vs. "outcomes" —
			// see queue.ts:386-396 in `loadPhoneCallOutcomes`. We treat anything
			// that isn't TopShortcuts/Shortcuts as an outcome group.
			const otherGroups = groups.filter(g => g.name !== FieldKey.TopShortcuts && g.name !== FieldKey.Shortcuts);
			const meetingOutcomes = otherGroups.find(g => g.id === FieldKey.MeetingOutcomes);
			const connectedGroup = otherGroups.find(g => g.name === FieldKey.Connected);

			const flattened: PhoneCallOutcome[] = [
				...(topShortcuts?.children ?? []),
				...(meetingOutcomes?.children ?? []),
				...otherGroups.filter(g => g.isShortCut && g.name !== FieldKey.BadLead),
				...(shortcuts?.children ?? []),
			];

			// `queue.badLead` (queue.ts:266-268) reads from the top-level outcomes
			// list, but production payloads place BadLead as a child of
			// `TopShortcuts` so it surfaces in the picker. Search both: top-level
			// first, then any of the surfaced children.
			const badLeadOutcome =
				otherGroups.find(g => g.name === FieldKey.BadLead) ?? flattened.find(o => o.name === FieldKey.BadLead);

			// Connected is a top-level group, not a leaf shortcut. The legacy
			// surfaces it only if `isShortCut === true`; many tenants don't set
			// that flag, so we inject it explicitly when missing so the rep can
			// always drill into Connected's children.
			const hasConnectedEntry = connectedGroup
				? flattened.some(o => o.id === connectedGroup.id || o.name === FieldKey.Connected)
				: false;
			const outcomeShortcuts = connectedGroup && !hasConnectedEntry ? [connectedGroup, ...flattened] : flattened;

			return { outcomeShortcuts, connectedGroup, badLeadOutcome };
		},
	});

	return {
		outcomeShortcuts: query.data?.outcomeShortcuts ?? [],
		connectedGroup: query.data?.connectedGroup,
		badLeadOutcome: query.data?.badLeadOutcome,
		isLoading: query.isLoading,
		error: query.error ?? null,
	};
}
